import { React, useState } from 'react';
import { connect, useSelector } from 'react-redux';
import { bindActionCreators, compose } from 'redux';

import './SellSteps.scss';

import ErrorSlideIn from '../../../components/Shared/ErrorSlideIn/ErrorSlideIn';
import DeliveryMethodChosen from '../DeliveryMethodChosen/DeliveryMethodChosen';
import DeliveryMethodForm from '../DeliveryMethodForm/DeliveryMethodForm';
import ProgressStep from '../../ProgressStep/ProgressStep';
import CheckBox from 'src/components/Shared/Checkbox/Checkbox';
import { moduleActions } from '../../../redux/modules';
import {
  isCustomerWebViewEnabled,
  isSalesforceRep as isSalesforceRepEnabled,
  isEmbeddedInIFrame,
} from '../../../redux/modules/authentication/authentication.module.selectors';
import {
  getIsStep1NextButtonDisabled,
  getSellTransactionIsValid,
  getHasAutoExchangesOrAutoSell,
  getSellTransactionLookup,
} from '../../../redux/modules/sellTransaction/sellTransaction.selectors';
import {
  getRedemptionPayload,
  getPrintData,
} from '../../../redux/modules/executeRedemption/executeRedemption.selectors';
import { getSystematicActivityRMDs } from '../../../redux/modules/RMD/RMDSetting.selectors';
import { isSelectedFundRetirement as isSelectedRetirementFund } from 'src/redux/modules/sellTransaction/sellTransaction.selectors';
import {
  CANCEL_TRADE_CONFIRMATION_MODAL,
  DISTRIBUTION_TRANSACTIONS,
} from '../../../redux/modules/modals/modals.module';
import SellFundList from '../SellFundList/SellFundList';
import SellFundSummary from '../SellFundSummary/SellFundSummary';
import SellReview from '../SellReview/SellReview';
import AppTitle from '../../Shared/AppTitle/AppTitle';
import TRP from '../../Shared/TRP/TRP';
import { scrollElementIntoFullView } from '../../../utils/dom-utils';
import If from '../../Shared/If/If';
import { getAutoSellWithholdingFunds } from 'src/redux/modules/sellTransaction/selectors';

const SellSteps = (props) => {
  const [shouldValidate, setShouldValidate] = useState(false);
  const [RMDImpactsDisplayed, setRMDImpactsDisplayed] = useState(false);
  const [isRepSelected, setIsRepSelected] = useState(false);

  const currentStep = useSelector((state) => state.progress.currentStep ?? 0);
  const isIFrame = useSelector(isEmbeddedInIFrame);
  const {
    redemption: { redemptionResultLoading },
    validation: {
      isValidRedemption,
      validationMessage: { message },
      validationResultLoaded,
      validationResultLoading,
    },
  } = useSelector((state) => state.executeRedemption);
  const isSalesforceRep = useSelector(isSalesforceRepEnabled);
  const isSelectedFundRetirement = useSelector(isSelectedRetirementFund);
  const customerWebViewEnabled = useSelector(isCustomerWebViewEnabled);
  const existingRMD = useSelector(getSystematicActivityRMDs);
  const printData = useSelector(getPrintData);
  const sellTransactionIsValid = useSelector(getSellTransactionIsValid);
  const isStep1NextButtonDisabled = useSelector(getIsStep1NextButtonDisabled);
  const redemptionPayload = useSelector(getRedemptionPayload);
  const validatedSellFundsList = useSelector(getAutoSellWithholdingFunds);
  const hasAutoExchangesOrAutoSell = useSelector(getHasAutoExchangesOrAutoSell);
  const sellLookUp = useSelector(getSellTransactionLookup);

  function ownsRMDEligble(onlyOWNRRMDs) {
    if (onlyOWNRRMDs.length > 0) {
      return true;
    }
    return false;
  }

  function sellStepValidation() {
    const {
      dispatch,
      actions: {
        setValidationStart,
        setValidationFailed,
        setValidationSuccessful,
        validateSingleRedemption,
      },
    } = props;
    setValidationStart();

    const transactionsValidationArray = redemptionPayload.map((transaction) => {
      return dispatch(validateSingleRedemption(JSON.stringify(transaction)));
    });

    Promise.all(transactionsValidationArray).then(
      () => {
        setValidationSuccessful();
        setShouldValidate(false);
        setRMDImpactsDisplayed(false);
      },
      (e) => {
        setValidationFailed(e);
      }
    );
  }

  function onClickScroll(step, element) {
    const {
      actions: { setProgress },
    } = props;
    setProgress(step);
    scrollElementIntoFullView(element, 15);
  }

  function handleTradeDate() {
    const {
      dispatch,
      actions: { getEligibleReddemTradeDates },
    } = props;
    const payload = redemptionPayload.map((transaction) => {
      return {
        ...transaction,
        sourceAgreement: {
          ...transaction.sourceAgreement,
          trpCode: sellLookUp[transaction.sourceAgreement.agreementID]?.trpCode,
        },
      };
    });
    const validTradeDates = payload.map((transaction) => {
      return dispatch(getEligibleReddemTradeDates(transaction));
    });

    Promise.all(validTradeDates).then(() => {
      onClickScroll(3, 'funding-list-step');
    });
  }

  const {
    submitRedemption,
    actions: { openModal, saveRawPrintData },
  } = props;
  const sellWithholdingWarned = validatedSellFundsList?.some(
    (fund) => !fund?.nonRetirementLike
  );
  return (
    <form onSubmit={(e) => e.preventDefault()} className="animated fadeInUp">
      <If test={!(isIFrame || (!customerWebViewEnabled && isSalesforceRep))}>
        <AppTitle
          title="Sell Mutual Funds"
          subtitle={
            <span>
              Sell your <TRP /> mutual funds and have the proceeds sent to you.
            </span>
          }
          mboxTitle="ret-aa-new-mf-sell-title"
          mboxSubtitle="ret-aa-new-mf-sell-subtitle"
        />
      </If>

      <ProgressStep
        title={
          <span>
            Choose<span className="hidden-xs hidden-sm"> your asset</span>{' '}
            delivery method
          </span>
        }
        className="funding-target-step"
        clickable={currentStep > 1}
        step={1}
        active={currentStep === 1}
        complete={currentStep > 1}
        OnClickHeader={() => {
          currentStep > 1 && onClickScroll(1, 'funding-target-step');
        }}
        expanded
        nextButtonLabel={
          <span>
            <b>NEXT: </b>Select
            <span className="hidden-xs hidden-sm"> your</span> fund(s) and
            amounts
          </span>
        }
        nextButtonDisabled={isStep1NextButtonDisabled}
        onNextClick={() => {
          onClickScroll(2, 'funding-target-step');
        }}
        showCancelLink={!(!customerWebViewEnabled && isSalesforceRep)}
        onCancelClick={() => openModal(CANCEL_TRADE_CONFIRMATION_MODAL)}
      >
        {currentStep === 1 ? (
          <DeliveryMethodForm />
        ) : (
          <DeliveryMethodChosen
            onEditClick={() => {
              onClickScroll(1, 'funding-target-step');
            }}
          />
        )}
      </ProgressStep>

      <ProgressStep
        title={
          <span>
            Select your fund(s) <span className="hidden-xs"> and amounts</span>
          </span>
        }
        className="fund-list-step"
        clickable={
          currentStep > 2 || (currentStep !== 2 && sellTransactionIsValid)
        }
        step={2}
        active={currentStep === 2}
        complete={currentStep > 2}
        OnClickHeader={() => {
          (currentStep > 2 || (currentStep !== 2 && sellTransactionIsValid)) &&
            !isStep1NextButtonDisabled &&
            onClickScroll(2, 'funding-target-step');
        }}
        expanded
        nextButtonClassName="e2e-step2-verify-submit"
        nextButtonLabel={
          <span>
            <b>NEXT:</b> Review & Submit
          </span>
        }
        checkbox={
          currentStep === 2 &&
          !customerWebViewEnabled &&
          isSalesforceRep &&
          sellWithholdingWarned ? (
            <div className="sell-fund-availiblity-check-con">
              {' '}
              <CheckBox
                inline={true}
                checked={isRepSelected}
                onChange={(e) => {
                  setIsRepSelected(e);
                }}
              />
              <span className="sell-fund-availiblity-check-text">
                I have read the current{' '}
                <a
                  target="_blank"
                  rel="noreferrer"
                  href="https://troweprice.egain.cloud/kb/retail/content/202200000002595/IRA-Redemption-Disclosures"
                >
                  Federal Withholding Disclosures
                </a>
                . Note: Disclosure must be read whether federal withholding
                elected or not
              </span>
            </div>
          ) : (
            <></>
          )
        }
        nextButtonLoading={validationResultLoading}
        nextButtonDisabled={
          !sellTransactionIsValid ||
          (!isRepSelected &&
            !customerWebViewEnabled &&
            isSalesforceRep &&
            sellWithholdingWarned)
        }
        onNextClick={() => {
          const ownsRMD = ownsRMDEligble(existingRMD);
          if (
            (hasAutoExchangesOrAutoSell || ownsRMD) &&
            isSelectedFundRetirement &&
            !RMDImpactsDisplayed
          ) {
            setRMDImpactsDisplayed(true);
            openModal(DISTRIBUTION_TRANSACTIONS);
          } else {
            handleTradeDate();
          }
          if (shouldValidate) {
            sellStepValidation();
          }
        }}
        showCancelLink={!(!customerWebViewEnabled && isSalesforceRep)}
        onCancelClick={() => openModal(CANCEL_TRADE_CONFIRMATION_MODAL)}
      >
        {currentStep === 2 ? (
          <SellFundList currentStep={currentStep} />
        ) : (
          <SellFundSummary
            clickable={sellTransactionIsValid}
            onEditClick={() => {
              onClickScroll(2, 'funding-target-step');
            }}
          />
        )}
      </ProgressStep>

      <ErrorSlideIn
        msg={message}
        show={validationResultLoaded && !isValidRedemption}
      />

      <ProgressStep
        title={
          <span>
            Review <span className="visible-xs-inline">& </span>
            <span className="hidden-xs">and </span>submit{' '}
            <span className="hidden-xs"> your trade</span>
          </span>
        }
        className="review-step"
        clickable={currentStep > 3}
        step={3}
        active={currentStep === 3}
        OnClickHeader={() => {
          currentStep >= 3 && onClickScroll(3, 'fund-list-step');
        }}
        expanded={currentStep === 3}
        nextButtonLabel="SUBMIT"
        nextButtonDisabled={customerWebViewEnabled || redemptionResultLoading}
        onNextClick={() => {
          saveRawPrintData(printData);
          submitRedemption();
        }}
        showCancelLink={!(!customerWebViewEnabled && isSalesforceRep)}
        onCancelClick={() => openModal(CANCEL_TRADE_CONFIRMATION_MODAL)}
      >
        {currentStep === 3 ? <SellReview /> : null}
      </ProgressStep>
    </form>
  );
};

const mapStateToProps = () => {};

const mapDispatchToProps = (dispatch) => ({
  actions: bindActionCreators(
    {
      openModal: moduleActions.openModal,
      saveRawPrintData: moduleActions.saveRawPrintData,
      setProgress: moduleActions.setProgress,
      setValidationStart: moduleActions.setValidationStart,
      setValidationFailed: moduleActions.setValidationFailed,
      setValidationSuccessful: moduleActions.setValidationSuccessful,
      validateSingleRedemption: moduleActions.validateSingleRedemption,
      getEligibleReddemTradeDates: moduleActions.getEligibleReddemTradeDates,
      validationResultLoaded: moduleActions.validationResultLoaded,
    },
    dispatch
  ),
  dispatch,
});

export default compose(connect(mapStateToProps, mapDispatchToProps))(SellSteps);
